import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Download, Hash, LayoutGrid, LogOut, Menu as MenuIcon, PanelLeftClose, PanelLeftOpen, Plus, RefreshCw, Search, Settings, Smartphone, Star, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from "react-router";
import { useLiveQuery } from "dexie-react-hooks";
import { colorHex, type Project } from "@todoer/shared";
import { signOut } from "../lib/auth.js";
import { clearLocalData, db, useUser } from "../lib/db.js";
import { elapsedMs, fmtClock, remainingMs, startFocusTicker, useFocus } from "../lib/focus.js";
import { useNow } from "../lib/hooks.js";
import { dismissInstall, install, isStandalone, startInstall } from "../lib/install.js";
import { cmd, dispatch, rejectedListeners, sync } from "../lib/sync.js";
import { applyTheme } from "../lib/theme.js";
import { dismissToast, showToast, useToast } from "../lib/toast.js";
import { BulkBar } from "./BulkBar.js";
import { CommandPalette, ShortcutsHelp } from "./CommandPalette.js";
import { InstallGuide } from "./InstallGuide.js";
import { NAV_FOOTER, NAV_PRIMARY, NAV_TOOLS, NavIcon, NotificationsButton, SyncStatus, TodayIcon, useNavData, type NavDef } from "./nav.js";
import { ConfirmHost, MenuList, overlayOpen, Popover, TooltipLayer, usePopover } from "./overlay.js";
import { ProjectDialog } from "./ProjectDialogs.js";
import { QuickAdd } from "./QuickAdd.js";
import { TaskDetail } from "./TaskDetail.js";
import { Avatar, Button, compactQuery, cx, Dot, IconButton, Kbd, narrowQuery, pageTitle, Spinner, useIsPhone, useMedia } from "./ui.js";

const GO_KEYS: Record<string, string> = {
  i: "/inbox", t: "/today", u: "/upcoming", c: "/calendar", f: "/filters", m: "/matrix", h: "/habits", o: "/focus", p: "/productivity", s: "/settings",
};

/** Opens the quick add from anywhere (e.g. the Today empty state). */
export const openQuickAdd = () => window.dispatchEvent(new Event("todoer:quick-add"));
export const openPalette = () => window.dispatchEvent(new Event("todoer:palette"));
export const openNewProject = (parentId: string | null = null) => window.dispatchEvent(new CustomEvent("todoer:new-project", { detail: parentId }));

export function Layout() {
  const user = useUser();
  const isPhone = useIsPhone();
  // Computer windows: wide = full sidebar (or the rail if chosen), compact = rail, narrow = menu button.
  // In compact and narrow windows the full sidebar opens as a drawer over the page.
  const compact = useMedia(compactQuery);
  const narrow = useMedia(narrowQuery);
  const [drawer, setDrawer] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [installGuide, setInstallGuide] = useState(false);
  const [newProject, setNewProject] = useState<{ parentId: string | null } | null>(null);
  const [rail, setRail] = useState(() => { try { return localStorage.getItem("todoer.sidebar") === "rail"; } catch { return false; } });
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const openTaskId = params.get("task");
  const closeTask = useCallback(() => setParams((p) => { p.delete("task"); p.delete("focus"); return p; }), [setParams]);
  const gPressed = useRef(0);
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => { startFocusTicker(); }, []);
  useEffect(() => { if (user) applyTheme(user.theme); }, [user?.theme]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Keep the server's timezone in step with this device so reminders arrive at the right local time.
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (user && tz && user.timezone !== tz) void dispatch(cmd("user_update", { timezone: tz }));
  }, [user?.timezone]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const l = (message: string) => showToast(`Couldn't save a change: ${message}`);
    rejectedListeners.add(l);
    return () => { rejectedListeners.delete(l); };
  }, []);
  useEffect(() => { mainRef.current?.scrollTo?.({ top: 0 }); }, [location.pathname]);
  useEffect(() => {
    const qa = () => setQuickAddOpen(true);
    const pal = () => setPaletteOpen(true);
    const np = (e: Event) => setNewProject({ parentId: (e as CustomEvent<string | null>).detail ?? null });
    window.addEventListener("todoer:quick-add", qa);
    window.addEventListener("todoer:palette", pal);
    const ig = () => setInstallGuide(true);
    window.addEventListener("todoer:new-project", np);
    window.addEventListener("todoer:install-guide", ig);
    return () => {
      window.removeEventListener("todoer:quick-add", qa); window.removeEventListener("todoer:palette", pal);
      window.removeEventListener("todoer:new-project", np); window.removeEventListener("todoer:install-guide", ig);
    };
  }, []);

  const toggleRail = useCallback(() => {
    if (compact) { setDrawer((d) => !d); return; }
    setRail((r) => {
      try { localStorage.setItem("todoer.sidebar", r ? "full" : "rail"); } catch { /* ignore */ }
      return !r;
    });
  }, [compact]);
  useEffect(() => setDrawer(false), [location.pathname, compact, isPhone]);
  useEffect(() => {
    if (!drawer) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); setDrawer(false); } };
    window.addEventListener("keydown", esc, true);
    return () => window.removeEventListener("keydown", esc, true);
  }, [drawer]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      const typing = el?.closest?.("input, textarea, select, [contenteditable=true]");
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPaletteOpen(true); return; }
      if (typing || e.metaKey || e.ctrlKey || e.altKey || overlayOpen()) return;
      if (Date.now() - gPressed.current < 1200 && GO_KEYS[e.key]) { e.preventDefault(); gPressed.current = 0; navigate(GO_KEYS[e.key]!); return; }
      if (e.key === "g") { gPressed.current = Date.now(); return; }
      if (e.key === "q") { e.preventDefault(); setQuickAddOpen(true); }
      else if (e.key === "/") { e.preventDefault(); setPaletteOpen(true); }
      else if (e.key === "?") { e.preventDefault(); setHelpOpen(true); }
      else if (e.key === "[") { e.preventDefault(); toggleRail(); }
      else if ((e.key === "j" || e.key === "k") && !params.get("task")) {
        const rows = Array.from(document.querySelectorAll<HTMLElement>("#main-content [data-task-id]"));
        if (!rows.length) return;
        e.preventDefault();
        const current = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>("[data-task-id]");
        const i = current ? rows.indexOf(current) : -1;
        const next = rows[i < 0 ? 0 : Math.max(0, Math.min(rows.length - 1, i + (e.key === "j" ? 1 : -1)))];
        next?.focus();
        next?.scrollIntoView({ block: "nearest" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, toggleRail, params]);

  const logout = async () => {
    await signOut();
    await clearLocalData();
    window.location.href = "/login";
  };

  return (
    <div className="flex h-dvh overflow-hidden bg-surface">
      <a href="#main-content" className="fixed left-3 top-3 z-[90] -translate-y-20 rounded-control bg-brand-strong px-3 py-2 text-body font-medium text-white focus:translate-y-0">Skip to content</a>
      {!isPhone && !narrow && <Sidebar rail={compact || rail} onToggleRail={toggleRail} onAdd={() => setQuickAddOpen(true)} onSearch={() => setPaletteOpen(true)} onNewProject={(parentId) => setNewProject({ parentId })} onLogout={logout} />}
      {!isPhone && compact && drawer && (
        <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true" aria-label="Navigation">
          <button type="button" aria-label="Close navigation" className="absolute inset-0 bg-backdrop anim-fade" onClick={() => setDrawer(false)} />
          <div className="relative h-full shadow-modal anim-drawer">
            <Sidebar rail={false} drawer onToggleRail={() => setDrawer(false)} onAdd={() => { setDrawer(false); setQuickAddOpen(true); }} onSearch={() => { setDrawer(false); setPaletteOpen(true); }}
              onNewProject={(parentId) => { setDrawer(false); setNewProject({ parentId }); }} onLogout={logout} />
          </div>
        </div>
      )}
      <main ref={mainRef} id="main-scroll" className={cx("relative min-w-0 flex-1 overflow-y-auto overscroll-y-contain transition-[padding] duration-200", openTaskId && "min-[1280px]:pr-[480px]")}>
        {isPhone && <PhoneTopBar />}
        {!isPhone && narrow && <NarrowTopBar onMenu={() => setDrawer(true)} onSearch={() => setPaletteOpen(true)} onAdd={() => setQuickAddOpen(true)} />}
        {isPhone ? <PullToRefresh scroller={mainRef} /> : null}
        <div id="main-content" tabIndex={-1} className="w-full max-w-[936px] px-12 pb-24 pt-10 outline-none max-lg:px-8 max-md:px-4 max-md:pt-6 phone:pb-[calc(96px+env(safe-area-inset-bottom))] phone:pt-1">
          <Outlet />
        </div>
      </main>
      {isPhone && <BottomTabs onAdd={() => setQuickAddOpen(true)} />}
      {quickAddOpen && <QuickAdd onClose={() => setQuickAddOpen(false)} projectId={location.pathname.startsWith("/project/") ? location.pathname.split("/")[2] : null} />}
      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} onQuickAdd={() => setQuickAddOpen(true)} onNewProject={() => setNewProject({ parentId: null })} onHelp={() => setHelpOpen(true)} />}
      {helpOpen && <ShortcutsHelp onClose={() => setHelpOpen(false)} />}
      {installGuide && <InstallGuide onClose={() => setInstallGuide(false)} />}
      {newProject && <ProjectDialog parentId={newProject.parentId} onClose={() => setNewProject(null)} onSaved={(id) => navigate(`/project/${id}`)} />}
      {openTaskId && <TaskDetail key={openTaskId} taskId={openTaskId} onClose={closeTask} />}
      <BulkBar />
      <Toast phone={isPhone} />
      <TooltipLayer />
      <ConfirmHost />
    </div>
  );
}

// ------------------------------------------------------------------ desktop sidebar
function NavItem({ def, count, rail }: { def: NavDef; count?: number; rail: boolean }) {
  return (
    <NavLink to={def.to} data-tip={rail || def.shortcut ? def.label : undefined} data-tip-key={def.shortcut} data-tip-side="right" aria-label={rail ? def.label : undefined}
      className={({ isActive }) => cx("group flex h-8 items-center gap-2.5 rounded-control text-body transition-colors duration-150",
        rail ? "w-10 justify-center" : "px-2",
        isActive ? "bg-brand-soft font-medium text-brand-text" : "text-text-muted hover:bg-hover hover:text-text")}>
      <NavIcon icon={def.icon} />
      {!rail && <span className="min-w-0 flex-1 truncate">{def.label}</span>}
      {!rail && !!count && <span className="tabular text-meta text-text-faint">{count}</span>}
    </NavLink>
  );
}

function ProjectRow({ p, depth, count, rail, hasKids, collapsed, onToggle }: { p: Project; depth: number; count?: number; rail: boolean; hasKids?: boolean; collapsed?: boolean; onToggle?: () => void }) {
  return (
    <div className="relative flex items-center" style={{ paddingLeft: rail ? 0 : depth * 12 }}>
      {!rail && depth > 0 && <span aria-hidden className="absolute bottom-0 top-0 w-px bg-border" style={{ left: depth * 12 - 3 }} />}
      <NavLink to={`/project/${p.id}`} data-tip={rail ? p.name : undefined} data-tip-side="right" aria-label={rail ? p.name : undefined}
        className={({ isActive }) => cx("group flex h-8 min-w-0 flex-1 items-center gap-2.5 rounded-control text-body transition-colors duration-150",
          rail ? "w-10 flex-none justify-center" : "pl-2 pr-1", isActive ? "bg-brand-soft font-medium text-brand-text" : "text-text-muted hover:bg-hover hover:text-text")}>
        <Hash size={16} aria-hidden style={{ color: colorHex(p.color) }} />
        {!rail && <span className="min-w-0 flex-1 truncate">{p.name}</span>}
        {!rail && !!count && <span className="tabular text-meta text-text-faint group-hover:hidden">{count}</span>}
        {!rail && hasKids && (
          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle?.(); }} aria-label={collapsed ? `Expand ${p.name}` : `Collapse ${p.name}`} aria-expanded={!collapsed}
            className={cx("grid h-6 w-6 place-items-center rounded text-text-faint hover:bg-hover hover:text-text", !count && "", count ? "hidden group-hover:grid" : "")}>
            {collapsed ? <ChevronRight size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
          </button>
        )}
      </NavLink>
    </div>
  );
}

function Sidebar({ rail, drawer = false, onToggleRail, onAdd, onSearch, onNewProject, onLogout }: {
  rail: boolean; drawer?: boolean; onToggleRail: () => void; onAdd: () => void; onSearch: () => void; onNewProject: (parentId: string | null) => void; onLogout: () => void;
}) {
  const user = useUser();
  const navigate = useNavigate();
  const nav = useNavData();
  const userMenu = usePopover<HTMLButtonElement>();
  const [projectsOpen, setProjectsOpen] = useState(() => { try { return localStorage.getItem("todoer.projectsOpen") !== "0"; } catch { return true; } });
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem("todoer.collapsed") ?? "[]")); } catch { return new Set(); }
  });
  const toggle = (id: string) => setCollapsed((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    try { localStorage.setItem("todoer.collapsed", JSON.stringify([...n])); } catch { /* ignore */ }
    return n;
  });
  const visibleTree = nav.tree.filter((p) => {
    for (let parent = p.parentId; parent; parent = nav.tree.find((x) => x.id === parent)?.parentId ?? null) if (collapsed.has(parent)) return false;
    return true;
  });
  const countFor = (def: NavDef) => (def.countKey ? nav.counts[def.countKey] : undefined);
  const hasFavs = nav.favProjects.length > 0 || nav.favLabels.length > 0 || nav.favFilters.length > 0;
  const groupLabel = (text: string) => !rail && <div className="px-2 pb-1 pt-4 text-caption font-semibold uppercase text-text-faint">{text}</div>;

  return (
    <aside aria-label="Sidebar" className={cx("flex h-full shrink-0 flex-col border-r border-border bg-surface-raised transition-[width] duration-200 ease-out", rail ? "w-16" : "w-[272px]")}>
      <div className={cx("flex h-14 shrink-0 items-center gap-1 px-3", rail && "flex-col justify-center px-0")}>
        {!rail && (
          <button type="button" onClick={userMenu.toggle} aria-label="Account menu" aria-expanded={userMenu.isOpen}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-control px-1.5 py-1 text-left hover:bg-hover">
            <Avatar person={user} size={26} />
            <span className="truncate text-body font-semibold">{user?.name}</span>
            <ChevronDown size={14} className="shrink-0 text-text-faint" aria-hidden />
          </button>
        )}
        {!rail && <NotificationsButton />}
        <IconButton icon={drawer ? X : rail ? PanelLeftOpen : PanelLeftClose} label={drawer ? "Close navigation" : rail ? "Expand sidebar" : "Collapse sidebar"} shortcut={drawer ? "Esc" : "["} onClick={onToggleRail} />
      </div>
      {userMenu.anchor && (
        <Popover anchor={userMenu.anchor} onClose={userMenu.close} title="Account" width={240}>
          <div className="border-b border-border px-3 py-2.5"><div className="truncate text-body font-semibold">{user?.name}</div><div className="truncate text-meta text-text-faint">{user?.email}</div></div>
          <MenuList label="Account" onClose={userMenu.close} items={[
            { label: "Settings", icon: Settings, shortcut: "G S", onSelect: () => navigate("/settings") },
            ...(isStandalone() ? [] : [{ label: "Install the app", icon: Download, onSelect: () => void startInstall() }]),
            { label: "Sign out", icon: LogOut, onSelect: onLogout },
          ]} />
        </Popover>
      )}

      <div className={cx("space-y-0.5 px-3", rail && "flex flex-col items-center px-0")}>
        <button type="button" onClick={onAdd} data-tip={rail ? "Add task" : undefined} data-tip-key="Q" data-tip-side="right" aria-label={rail ? "Add task" : undefined}
          className={cx("group flex h-9 items-center gap-2.5 rounded-control text-body font-semibold text-brand-text transition-colors hover:bg-brand-soft", rail ? "w-10 justify-center" : "w-full px-2")}>
          <span className="grid h-[22px] w-[22px] place-items-center rounded-full bg-brand-strong text-white"><Plus size={16} strokeWidth={2.5} aria-hidden /></span>
          {!rail && <span className="flex-1 text-left">Add task</span>}
          {!rail && <Kbd className="opacity-0 transition-opacity group-hover:opacity-100">Q</Kbd>}
        </button>
        <button type="button" onClick={onSearch} data-tip={rail ? "Search" : undefined} data-tip-key="Ctrl K" data-tip-side="right" aria-label="Search"
          className={cx("flex h-8 items-center gap-2.5 rounded-control text-body text-text-faint transition-colors", rail ? "w-10 justify-center hover:bg-hover" : "w-full border border-border bg-surface px-2 hover:border-border-strong")}>
          <Search size={16} aria-hidden />
          {!rail && <><span className="flex-1 text-left">Search</span><Kbd>Ctrl K</Kbd></>}
        </button>
      </div>

      <nav aria-label="Main" className={cx("mt-2 min-h-0 flex-1 overflow-y-auto px-3 pb-3", rail && "flex flex-col items-center px-0")}>
        <FocusIndicator rail={rail} />
        <div className={cx("space-y-0.5", rail && "flex flex-col items-center")}>
          {NAV_PRIMARY.map((d) => <NavItem key={d.to} def={d} count={countFor(d)} rail={rail} />)}
        </div>
        <div className={cx("my-2 h-px bg-border", rail ? "w-8" : "mx-2")} role="separator" />
        <div className={cx("space-y-0.5", rail && "flex flex-col items-center")}>
          {NAV_TOOLS.map((d) => <NavItem key={d.to} def={d} rail={rail} />)}
        </div>

        {hasFavs && (
          <>
            {groupLabel("Favourites")}
            {rail && <div className="my-2 h-px w-8 bg-border" role="separator" />}
            <div className={cx("space-y-0.5", rail && "flex flex-col items-center")}>
              {nav.favProjects.map((p) => <ProjectRow key={`f${p.id}`} p={p} depth={0} count={nav.counts.byProject.get(p.id)} rail={rail} />)}
              {nav.favFilters.map((f) => (
                <NavLink key={f.id} to={`/filter/${f.id}`} data-tip={rail ? f.name : undefined} data-tip-side="right" aria-label={rail ? f.name : undefined}
                  className={({ isActive }) => cx("flex h-8 items-center gap-2.5 rounded-control text-body", rail ? "w-10 justify-center" : "px-2", isActive ? "bg-brand-soft font-medium text-brand-text" : "text-text-muted hover:bg-hover hover:text-text")}>
                  <Star size={16} aria-hidden style={{ color: colorHex(f.color) }} />{!rail && <span className="truncate">{f.name}</span>}
                </NavLink>
              ))}
              {nav.favLabels.map((l) => (
                <NavLink key={l.id} to={`/label/${l.id}`} data-tip={rail ? `@${l.name}` : undefined} data-tip-side="right" aria-label={rail ? `@${l.name}` : undefined}
                  className={({ isActive }) => cx("flex h-8 items-center gap-2.5 rounded-control text-body", rail ? "w-10 justify-center" : "px-2", isActive ? "bg-brand-soft font-medium text-brand-text" : "text-text-muted hover:bg-hover hover:text-text")}>
                  <span aria-hidden className="w-4 text-center font-semibold" style={{ color: colorHex(l.color) }}>@</span>{!rail && <span className="truncate">{l.name}</span>}
                </NavLink>
              ))}
            </div>
          </>
        )}

        {!rail ? (
          <div className="group/projects flex items-center pr-1 pt-4">
            <button type="button" onClick={() => setProjectsOpen((o) => { try { localStorage.setItem("todoer.projectsOpen", o ? "0" : "1"); } catch { /* ignore */ } return !o; })}
              aria-expanded={projectsOpen} className="flex flex-1 items-center gap-1 rounded-control px-2 py-1 text-caption font-semibold uppercase text-text-faint hover:text-text">
              My Projects {projectsOpen ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}
            </button>
            <IconButton size="sm" icon={Plus} label="Add project" onClick={() => onNewProject(null)} />
          </div>
        ) : <div className="my-2 h-px w-8 bg-border" role="separator" />}
        {(projectsOpen || rail) && (
          <div className={cx("space-y-0.5", rail && "flex flex-col items-center")}>
            {(rail ? nav.tree.filter((p) => p.depth === 0) : visibleTree).map((p) => (
              <ProjectRow key={p.id} p={p} depth={rail ? 0 : p.depth} count={nav.counts.byProject.get(p.id)} rail={rail}
                hasKids={nav.tree.some((x) => x.parentId === p.id)} collapsed={collapsed.has(p.id)} onToggle={() => toggle(p.id)} />
            ))}
            {nav.loaded && nav.tree.length === 0 && !rail && (
              <button type="button" onClick={() => onNewProject(null)} className="flex h-8 w-full items-center gap-2.5 rounded-control px-2 text-body text-text-faint hover:bg-hover hover:text-text">
                <Plus size={16} aria-hidden />Add your first project
              </button>
            )}
            {rail && <IconButton icon={Plus} label="Add project" onClick={() => onNewProject(null)} />}
          </div>
        )}
      </nav>

      <div className={cx("shrink-0 border-t border-border px-3 py-2", rail && "flex flex-col items-center gap-1 px-0")}>
        <div className={cx("flex items-center gap-0.5", rail && "flex-col")}>
          {NAV_FOOTER.map((d) => (
            <NavLink key={d.to} to={d.to} aria-label={d.label} data-tip={d.label} data-tip-key={d.shortcut} data-tip-side={rail ? "right" : undefined}
              className={({ isActive }) => cx("grid h-8 w-8 place-items-center rounded-control transition-colors", isActive ? "bg-brand-soft text-brand-text" : "text-text-muted hover:bg-hover hover:text-text")}>
              <NavIcon icon={d.icon} />
            </NavLink>
          ))}
          {!rail && <SyncStatus className="ml-auto" />}
        </div>
        {!rail && <DesktopInstall />}
      </div>
    </aside>
  );
}

function FocusIndicator({ rail }: { rail: boolean }) {
  const s = useFocus();
  useNow(1000);
  if (!s.running && !s.elapsedMs) return null;
  const ms = s.mode === "pomodoro" ? remainingMs(s) : elapsedMs(s);
  return (
    <NavLink to="/focus" data-tip={rail ? "Focus timer" : undefined} data-tip-side="right"
      className={cx("mb-2 flex h-9 items-center gap-2.5 rounded-control bg-brand-soft text-body font-medium text-brand-text", rail ? "w-10 justify-center" : "px-2")}>
      <span aria-hidden className={cx("h-2 w-2 shrink-0 rounded-full bg-brand", s.running && "animate-pulse")} />
      {!rail ? <><span className="flex-1">{s.phase === "focus" ? "Focus" : "Break"}</span><span className="tabular">{fmtClock(ms)}</span>{!s.running && <span className="text-meta">paused</span>}</> : <span className="sr-only">{fmtClock(ms)}</span>}
    </NavLink>
  );
}

/** Top bar for narrow computer windows: the sidebar is behind the menu button. */
function NarrowTopBar({ onMenu, onSearch, onAdd }: { onMenu: () => void; onSearch: () => void; onAdd: () => void }) {
  const t = pageTitle.use();
  return (
    <div className="sticky top-0 z-20 flex h-12 items-center gap-1 border-b border-border bg-surface-raised/90 px-2 backdrop-blur-xl">
      <IconButton icon={MenuIcon} label="Open navigation" shortcut="[" onClick={onMenu} />
      <span className="min-w-0 flex-1 truncate px-1 text-heading font-semibold">{t.hidden ? t.text : "Todoer"}</span>
      <IconButton icon={Search} label="Search" shortcut="Ctrl K" onClick={onSearch} />
      <NotificationsButton />
      <button type="button" onClick={onAdd} aria-label="Add task" data-tip="Add task" data-tip-key="Q"
        className="ml-1 grid h-8 w-8 place-items-center rounded-full bg-brand-strong text-white transition-transform active:scale-95"><Plus size={18} strokeWidth={2.5} aria-hidden /></button>
    </div>
  );
}

// ------------------------------------------------------------------ phone
/** Pages that are tabs on the phone; every other page shows a back button. */
const PHONE_ROOTS = ["/today", "/upcoming", "/browse", "/search"];

/** Where Back goes when there is no in-app history (e.g. opened from a notification). */
export function phoneBackTarget(pathname: string, parentProject?: { id: string; name: string } | null): { to: string; label: string } | null {
  if (PHONE_ROOTS.includes(pathname)) return null;
  if (pathname.startsWith("/settings/")) return { to: "/settings", label: "Settings" };
  if (pathname.startsWith("/label/") || pathname.startsWith("/filter/")) return { to: "/filters", label: "Filters & Labels" };
  if (pathname.startsWith("/project/") && parentProject) return { to: `/project/${parentProject.id}`, label: parentProject.name };
  return { to: "/browse", label: "Browse" };
}

function PhoneTopBar() {
  const t = pageTitle.use();
  const location = useLocation();
  const navigate = useNavigate();
  const projectId = location.pathname.startsWith("/project/") ? location.pathname.split("/")[2] : undefined;
  const parent = useLiveQuery(async () => {
    if (!projectId) return null;
    const p = await db.projects.get(projectId);
    const parentProject = p?.parentId ? await db.projects.get(p.parentId) : undefined;
    return parentProject ? { id: parentProject.id, name: parentProject.name } : null;
  }, [projectId]);
  const target = phoneBackTarget(location.pathname, parent);
  // Go back through real history when there is some (location.key is "default" on the first page).
  const back = () => (location.key !== "default" ? navigate(-1) : target && navigate(target.to, { replace: true }));
  return (
    <div className={cx("sticky top-0 z-20 flex h-[calc(44px+env(safe-area-inset-top))] items-end justify-center px-12 pb-2.5 pt-safe transition-[background-color,border-color] duration-200",
      t.hidden ? "border-b border-border bg-surface-raised/85 backdrop-blur-xl" : "border-b border-transparent bg-surface")}>
      {target && (
        <button type="button" onClick={back} aria-label={`Back to ${location.key !== "default" ? "previous page" : target.label}`}
          className="absolute bottom-0 left-1 flex h-11 max-w-[40vw] items-center gap-0.5 rounded-control pl-1 pr-2 text-body font-medium text-brand-text active:bg-pressed">
          <ChevronLeft size={24} strokeWidth={2.25} className="shrink-0" aria-hidden />
          <span className="truncate">{location.key !== "default" ? "Back" : target.label}</span>
        </button>
      )}
      <span className={cx("truncate text-heading font-semibold transition-opacity duration-200", target && "max-w-[50vw]", t.hidden ? "opacity-100" : "opacity-0")} aria-hidden={!t.hidden}>{t.text}</span>
    </div>
  );
}

function PullToRefresh({ scroller }: { scroller: React.RefObject<HTMLElement | null> }) {
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const start = useRef<number | null>(null);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const down = (e: TouchEvent) => { start.current = el.scrollTop <= 0 && !overlayOpen(["dialog", "popover", "panel"]) ? e.touches[0]!.clientY : null; };
    const move = (e: TouchEvent) => {
      if (start.current === null || busy) return;
      const dy = e.touches[0]!.clientY - start.current;
      if (dy <= 0 || el.scrollTop > 0) { setPull(0); return; }
      setPull(Math.min(96, dy * 0.45));
    };
    const up = () => {
      if (start.current === null) return;
      start.current = null;
      setPull((p) => {
        if (p > 64) {
          setBusy(true);
          const min = new Promise((r) => setTimeout(r, 600));
          void Promise.all([sync(), min]).finally(() => { setBusy(false); setPull(0); });
          return 56;
        }
        return 0;
      });
    };
    el.addEventListener("touchstart", down, { passive: true });
    el.addEventListener("touchmove", move, { passive: true });
    el.addEventListener("touchend", up);
    el.addEventListener("touchcancel", up);
    return () => { el.removeEventListener("touchstart", down); el.removeEventListener("touchmove", move); el.removeEventListener("touchend", up); el.removeEventListener("touchcancel", up); };
  }, [scroller, busy]);
  if (!pull && !busy) return null;
  return (
    <div className="pointer-events-none flex justify-center overflow-hidden transition-[height] duration-150" style={{ height: pull }} aria-live="polite">
      <span className="mt-2 grid h-8 w-8 place-items-center rounded-full border border-border bg-surface-raised shadow-pop">
        {busy ? <Spinner size={16} /> : <RefreshCw size={16} className="text-text-muted" style={{ transform: `rotate(${pull * 4}deg)`, opacity: Math.min(1, pull / 64) }} aria-hidden />}
      </span>
      {busy && <span className="sr-only">Refreshing</span>}
    </div>
  );
}

function BottomTabs({ onAdd }: { onAdd: () => void }) {
  const tab = (to: string, label: string, icon: React.ReactNode) => (
    <NavLink to={to} className={({ isActive }) => cx("flex h-14 flex-1 flex-col items-center justify-center gap-0.5 text-caption font-medium normal-case tracking-normal transition-colors", isActive ? "text-brand-text" : "text-text-muted")}>
      {icon}<span>{label}</span>
    </NavLink>
  );
  return (
    <nav aria-label="Tabs" className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface-raised/85 pb-safe backdrop-blur-xl">
      <div className="mx-auto flex max-w-lg items-stretch px-2">
        {tab("/today", "Today", <TodayIcon size={22} />)}
        {tab("/upcoming", "Upcoming", <CalendarDays size={22} aria-hidden />)}
        <div className="flex flex-1 items-start justify-center">
          <button type="button" onClick={onAdd} aria-label="Add task"
            className="-mt-4 grid h-14 w-14 place-items-center rounded-full bg-brand-strong text-white shadow-[0_6px_16px_-4px_color-mix(in_srgb,var(--color-brand)_60%,transparent)] ring-4 ring-surface transition-transform active:scale-95">
            <Plus size={26} strokeWidth={2.25} aria-hidden />
          </button>
        </div>
        {tab("/browse", "Browse", <LayoutGrid size={22} aria-hidden />)}
        {tab("/search", "Search", <Search size={22} aria-hidden />)}
      </div>
    </nav>
  );
}

function DesktopInstall() {
  const s = install.use();
  if (s.dismissed || !s.event || isStandalone()) return null;
  return (
    <div className="mt-2 flex items-center gap-2 rounded-card border border-border bg-surface p-2 text-meta">
      <Smartphone size={16} className="text-brand-text" aria-hidden />
      <button type="button" onClick={() => void startInstall()} className="flex-1 text-left font-medium hover:underline">Install the app</button>
      <IconButton icon={X} size="sm" label="Dismiss" onClick={dismissInstall} />
    </div>
  );
}

function Toast({ phone }: { phone: boolean }) {
  const toast = useToast();
  return (
    <div role="status" aria-live="polite" className="pointer-events-none fixed inset-x-0 z-[70]" style={{ bottom: phone ? "calc(80px + env(safe-area-inset-bottom))" : 24 }}>
      {toast && (
        <div key={toast.id} className="pointer-events-auto absolute left-1/2 flex w-max max-w-[calc(100vw-24px)] items-center gap-3 rounded-card bg-text py-2 pl-4 pr-2 text-body text-surface shadow-modal anim-toast" style={{ transform: "translateX(-50%)" }}>
          <span className="min-w-0 truncate">{toast.message}</span>
          {toast.action && (
            <button type="button" onClick={() => { void toast.action!.run(); dismissToast(); }} className="shrink-0 rounded-control px-2 py-1 font-semibold text-brand hover:bg-surface/10 phone:py-2.5">{toast.action.label}</button>
          )}
          <button type="button" onClick={dismissToast} aria-label="Dismiss" className="grid h-7 w-7 shrink-0 place-items-center rounded-control opacity-70 hover:bg-surface/10 hover:opacity-100 phone:h-10 phone:w-10"><X size={16} aria-hidden /></button>
        </div>
      )}
    </div>
  );
}

export { Dot };
